// MARCA Media - 2026-07-01
// Add a unique hidden line item property to subscription add-to-cart requests so
// each subscription renders as its own cart line instead of merging quantity.
// MARCA | 2026-09-08 | Featured product plan images swap the main gallery image when a selling plan is selected.

class MarcaSubscriptionWidget {
  constructor(container) {
    this.container = container;
    this.section = container.closest("section");
    this.sectionId = container.dataset.sectionId;
    this.form = this.section?.querySelector(`#product-form-${this.sectionId}`);
    this.variantInput = this.form?.querySelector('input[name="id"]');
    this.sellingPlanInput = null;
    this.variantOptions = this.section?.querySelector("variant-options");
    this.oneTimeInput = container.querySelector('[data-purchase-type="onetime"]');
    this.subscribeInput = container.querySelector(
      '[data-purchase-type="subscription"]',
    );
    this.subscriptionVariantRadios = Array.from(
      container.querySelectorAll("[data-subscription-variant-radio]"),
    );
    this.oneTimePriceEl = container.querySelector("[data-onetime-price]");
    this.subscriptionPriceEl = container.querySelector(
      "[data-subscription-price]",
    );
    this.subscriptionCompareEl = container.querySelector(
      "[data-subscription-compare-price]",
    );
    this.subscriptionVariantGrid = container.querySelector(
      "[data-subscription-variant-grid]",
    );
    this.subscriptionCard = container.querySelector(".marca-subscription-widget__card");
    this.subscribeOption = container.querySelector("[data-subscribe-option]");
    this.purchaseTypeRows = container.querySelectorAll("[data-purchase-type-option]");
    this.data = this.getWidgetData();
    this.isSyncing = false;
  }

  getWidgetData() {
    const dataNode = this.container.querySelector(".js-marca-subscription-data");
    if (!dataNode) return null;

    try {
      return JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error("Failed to parse subscription widget data", error);
      return null;
    }
  }

  init() {
    if (!this.data || !this.form || !this.variantInput) return;

    this.ensureSellingPlanInput();
    this.bindEvents();
    this.bindHabitV2Tabs();
    this.updateOneTimeBasePrice();
    this.activateSubscribe();
  }

  generateSubscriptionLineId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  ensureSubscriptionLineProperty() {
    if (!this.subscribeInput?.checked || this.sellingPlanInput?.disabled) {
      this.clearSubscriptionLineProperty();
      return;
    }

    let lineIdInput = this.form.querySelector(
      'input[name="properties[_subscription_line_id]"]',
    );
    if (!lineIdInput) {
      lineIdInput = document.createElement("input");
      lineIdInput.type = "hidden";
      lineIdInput.name = "properties[_subscription_line_id]";
      this.form.appendChild(lineIdInput);
    }

    // MARCA Media - 2026-07-01: regenerate on each submit so repeat adds stay separate lines.
    lineIdInput.value = this.generateSubscriptionLineId();
  }

  clearSubscriptionLineProperty() {
    this.form
      ?.querySelector('input[name="properties[_subscription_line_id]"]')
      ?.remove();
  }

  logDebug(action, details = {}) {
    console.log("[MARCA Subscription Widget]", {
      action,
      sectionId: this.sectionId,
      variantId: this.variantInput?.value || null,
      sellingPlan: this.sellingPlanInput?.disabled ? null : this.sellingPlanInput?.value || null,
      purchaseType: this.subscribeInput?.checked ? "subscription" : "onetime",
      ...details,
    });
  }

  bindEvents() {
    this.oneTimeInput?.addEventListener("change", () => this.onPurchaseTypeChange());
    this.subscribeInput?.addEventListener("change", () =>
      this.onPurchaseTypeChange(),
    );
    this.subscribeOption?.addEventListener("click", (event) => {
      const clickedVariantOption = event.target.closest(
        "[data-subscription-variant-option]",
      );
      if (clickedVariantOption) return;
      this.activateSubscribe();
    });
    this.subscriptionVariantRadios.forEach((radio) => {
      radio.addEventListener("change", () => this.onSubscriptionVariantChange(radio));
    });
    this.form?.addEventListener(
      "submit",
      () => {
        this.ensureSubscriptionLineProperty();
        this.logDebug("submit", {
          selectedSubscriptionVariant:
            this.subscriptionVariantRadios.find((radio) => radio.checked)?.dataset
              .variantId || null,
        });
      },
      { capture: true },
    );

    document.addEventListener("variantChangeEnd", (event) => {
      if (this.isSyncing) return;
      if (!this.section || !event.target || !this.section.contains(event.target)) return;
      this.syncFromActiveVariant();
    });
  }

  ensureSellingPlanInput() {
    this.sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
    if (!this.sellingPlanInput) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "selling_plan";
      input.disabled = true;
      this.form.appendChild(input);
      this.sellingPlanInput = input;
    }
  }

  getVariantPayload(variantId) {
    return this.data.variants?.[variantId] || null;
  }

  getMoney(cents) {
    if (typeof cents !== "number") return "";

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, this.data.moneyFormat);
    }

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: window.Shopify?.currency?.active || "GBP",
    }).format(cents / 100);
  }

  getFirstAvailableVariant() {
    const preferredId = String(this.data.firstAvailableVariantId || "");
    if (preferredId && this.getVariantPayload(preferredId)) {
      return this.getVariantPayload(preferredId);
    }

    return Object.values(this.data.variants || {}).find(
      (variant) => variant && variant.available,
    );
  }

  isExcludedSubscriptionVariant(variant) {
    const excludedDays = this.data.excludeDaysSupply || [];
    return excludedDays.includes(Number(variant?.daysSupply));
  }

  getFirstSubscriptionVariant() {
    const preferredId = String(this.data.firstSubscriptionVariantId || "");
    const preferredVariant = preferredId ? this.getVariantPayload(preferredId) : null;
    if (
      preferredVariant?.sellingPlan &&
      !this.isExcludedSubscriptionVariant(preferredVariant)
    ) {
      return preferredVariant;
    }

    return Object.values(this.data.variants || {}).find(
      (variant) =>
        variant &&
        variant.sellingPlan &&
        !this.isExcludedSubscriptionVariant(variant),
    );
  }

  updateOneTimeBasePrice() {
    const oneTimeVariant = this.getFirstAvailableVariant();
    if (!oneTimeVariant || !this.oneTimePriceEl) return;
    this.oneTimePriceEl.textContent = this.getMoney(oneTimeVariant.price);
  }

  getMediaGallery() {
    return document.getElementById(`MediaGallery-${this.sectionId}`);
  }

  // MARCA | 2026-09-08 | Read plan image IDs from a JSON map. Numbered data-plan-media-90 attributes are not readable via dataset.
  getPlanMediaMap(mediaGallery) {
    if (mediaGallery?.marcaPlanMediaMap) {
      return mediaGallery.marcaPlanMediaMap;
    }

    const planMediaMap = {};
    const rawMap =
      mediaGallery?.getAttribute("data-plan-media-map") ||
      mediaGallery?.dataset?.planMediaMap ||
      "";

    if (rawMap) {
      try {
        Object.assign(planMediaMap, JSON.parse(rawMap));
      } catch (error) {
        console.error("Failed to parse plan media map", error);
      }
    }

    mediaGallery?.querySelectorAll("[data-plan-days]").forEach((el) => {
      const days = el.getAttribute("data-plan-days");
      const mediaId =
        el.getAttribute("data-media-id") || el.getAttribute("data-slide-media-id");
      if (days && mediaId && !planMediaMap[days]) {
        planMediaMap[days] = mediaId;
      }
    });

    if (mediaGallery && Object.keys(planMediaMap).length) {
      mediaGallery.marcaPlanMediaMap = planMediaMap;
    }

    return planMediaMap;
  }

  getSelectedDaysSupply(variant) {
    if (variant?.daysSupply) return String(variant.daysSupply);

    const checkedRadio = this.subscriptionVariantRadios.find((radio) => radio.checked);
    return String(checkedRadio?.dataset.daysSupply || "");
  }

  applyGalleryOfferMode(mediaGallery, activeMediaId, hiddenMediaIds) {
    const apply = () => {
      if (typeof mediaGallery.setOfferMediaVisibility === "function") {
        mediaGallery.setOfferMediaVisibility(activeMediaId, hiddenMediaIds);
        return;
      }

      if (typeof mediaGallery.setActiveMedia === "function") {
        mediaGallery.setActiveMedia(activeMediaId, true);
      }
    };

    apply();
    window.requestAnimationFrame(apply);
    window.setTimeout(apply, 0);
  }

  setGalleryOfferMode(mode, variant) {
    const mediaGallery = this.getMediaGallery();
    if (!mediaGallery) return;

    const subscriptionMediaId = mediaGallery.dataset.subscriptionOfferMediaId || "";
    const oneTimeMediaId = mediaGallery.dataset.onetimeOfferMediaId || "";
    const planMediaMap = this.getPlanMediaMap(mediaGallery);
    const planMediaIds = Object.values(planMediaMap);
    const trialPlanMediaId = planMediaMap["30"];
    const selectedPlanMediaId = planMediaMap[this.getSelectedDaysSupply(variant)];
    const hiddenMediaIds = [];
    let activeMediaId = subscriptionMediaId || selectedPlanMediaId || oneTimeMediaId;

    if (!activeMediaId && !planMediaIds.length) return;

    // MARCA | 2026-09-24 | Schema plan images replace the first carousel slide for 30/90(/180) day selections.
    if (mode === "onetime") {
      if (trialPlanMediaId) {
        activeMediaId = trialPlanMediaId;
        if (subscriptionMediaId) hiddenMediaIds.push(subscriptionMediaId);
        if (oneTimeMediaId) hiddenMediaIds.push(oneTimeMediaId);
        planMediaIds.forEach((mediaId) => {
          if (mediaId !== trialPlanMediaId) hiddenMediaIds.push(mediaId);
        });
        mediaGallery.classList.add("is-plan-media-override");
      } else if (oneTimeMediaId) {
        activeMediaId = oneTimeMediaId;
        if (subscriptionMediaId) hiddenMediaIds.push(subscriptionMediaId);
        hiddenMediaIds.push(...planMediaIds);
        mediaGallery.classList.remove("is-plan-media-override");
      }
    } else if (selectedPlanMediaId) {
      activeMediaId = selectedPlanMediaId;
      if (subscriptionMediaId) hiddenMediaIds.push(subscriptionMediaId);
      if (oneTimeMediaId) hiddenMediaIds.push(oneTimeMediaId);
      planMediaIds.forEach((mediaId) => {
        if (mediaId !== selectedPlanMediaId) hiddenMediaIds.push(mediaId);
      });
      mediaGallery.classList.add("is-plan-media-override");
    } else if (subscriptionMediaId) {
      activeMediaId = subscriptionMediaId;
      if (oneTimeMediaId) hiddenMediaIds.push(oneTimeMediaId);
      hiddenMediaIds.push(...planMediaIds);
      mediaGallery.classList.remove("is-plan-media-override");
    }

    if (planMediaIds.length) {
      mediaGallery.classList.add("is-plan-media-ready");
    }

    if (!activeMediaId) return;
    this.applyGalleryOfferMode(mediaGallery, activeMediaId, hiddenMediaIds);
  }

  togglePurchaseTypeRows() {
    const isOneTime = Boolean(this.oneTimeInput?.checked);

    this.purchaseTypeRows.forEach((row) => {
      const isSubscribeRow = row.dataset.purchaseTypeOption === "subscribe";
      const isSelected = isSubscribeRow
        ? this.subscribeInput?.checked
        : this.oneTimeInput?.checked;
      row.classList.toggle("is-selected", Boolean(isSelected));
    });

    if (this.subscriptionCard) {
      this.subscriptionCard.classList.remove("is-collapsed");
    }

    this.container.classList.toggle("is-onetime-selected", isOneTime);
  }

  onPurchaseTypeChange() {
    if (this.subscribeInput?.checked) {
      this.activateSubscribe();
      return;
    }

    this.activateOneTime();
  }

  onSubscriptionVariantChange(radio) {
    if (!radio?.checked) return;
    this.subscribeInput.checked = true;
    this.applySubscriptionVariant(String(radio.dataset.variantId));
    this.syncHabitV2Ui(radio.dataset.daysSupply);
  }

  bindHabitV2Tabs() {
    this.habitV2Tabs = Array.from(this.container.querySelectorAll("[data-habit-v2-tab]"));
    if (!this.habitV2Tabs.length) return;

    this.habitV2Tabs.forEach((tab) => {
      tab.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const days = String(tab.dataset.days || "");
        const radio = this.subscriptionVariantRadios.find(
          (item) => String(item.dataset.daysSupply) === days,
        );
        if (!radio) return;
        radio.checked = true;
        this.onSubscriptionVariantChange(radio);
      });
    });
  }

  syncHabitV2Ui(daysSupply) {
    if (!this.habitV2Tabs?.length) return;
    const days = String(daysSupply || "");

    this.habitV2Tabs.forEach((tab) => {
      const isActive = String(tab.dataset.days) === days;
      tab.classList.toggle("is-selected", isActive);
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
    });

    const activeTab =
      this.habitV2Tabs.find((tab) => String(tab.dataset.days) === days) ||
      this.habitV2Tabs[0];
    if (!activeTab) return;

    const subtitleEl = this.container.querySelector("[data-habit-v2-subtitle]");
    const descriptionEl = this.container.querySelector("[data-habit-v2-description]");
    const subtitle = activeTab.dataset.subtitle || "";
    const description = activeTab.dataset.description || "";

    if (subtitleEl) subtitleEl.textContent = subtitle;
    if (descriptionEl) {
      descriptionEl.textContent = description;
      descriptionEl.classList.toggle("is-empty", description.trim() === "");
    }
  }

  applySubscriptionVariant(variantId) {
    const selectedVariant = this.getVariantPayload(String(variantId));
    if (!selectedVariant || !selectedVariant.sellingPlan) return;

    this.subscribeInput.checked = true;
    if (this.subscriptionVariantGrid) {
      this.subscriptionVariantGrid.hidden = false;
    }
    this.markSubscriptionVariant(selectedVariant.id);
    this.applyThemeVariant(selectedVariant);
    this.updateSubscriptionVisuals(selectedVariant);
    this.setSellingPlan(selectedVariant.sellingPlan.id);
    this.updateOneTimeBasePrice();
    this.togglePurchaseTypeRows();
    this.setGalleryOfferMode("subscription", selectedVariant);
    this.logDebug("subscription-variant-selected", {
      selectedSubscriptionVariant: selectedVariant.id,
      selectedSubscriptionPlan: selectedVariant.sellingPlan.id,
    });
  }

  syncFromActiveVariant() {
    const activeVariantId = String(this.variantInput?.value || "");
    const activeVariant = this.getVariantPayload(activeVariantId);
    if (!activeVariant) return;

    if (this.subscribeInput?.checked && activeVariant.sellingPlan) {
      this.updateSubscriptionVisuals(activeVariant);
      this.setSellingPlan(activeVariant.sellingPlan.id);
      this.markSubscriptionVariant(activeVariant.id);
      this.setGalleryOfferMode("subscription", activeVariant);
    } else if (!this.subscribeInput?.checked) {
      this.updateOneTimeBasePrice();
    }
  }

  markSubscriptionVariant(variantId) {
    this.subscriptionVariantRadios.forEach((radio) => {
      const isMatched = String(radio.dataset.variantId) === String(variantId);
      radio.checked = isMatched;
      const optionLabel = radio.closest("[data-subscription-variant-option]");
      if (optionLabel) {
        optionLabel.classList.toggle("is-selected", isMatched);
      }
    });
  }

  clearSubscriptionVariantSelection() {
    this.subscriptionVariantRadios.forEach((radio) => {
      radio.checked = false;
      const optionLabel = radio.closest("[data-subscription-variant-option]");
      if (optionLabel) {
        optionLabel.classList.remove("is-selected");
      }
    });
  }

  setSellingPlan(planId) {
    if (!this.sellingPlanInput) return;

    if (planId) {
      this.sellingPlanInput.value = String(planId);
      this.sellingPlanInput.disabled = false;
      return;
    }

    this.sellingPlanInput.value = "";
    this.sellingPlanInput.disabled = true;
  }

  updateSubscriptionVisuals(variant) {
    if (!variant?.sellingPlan) return;

    const plan = variant.sellingPlan;
    const basePrice = Number(variant.price);
    const salePrice = Number(plan.price);

    this.subscriptionPriceEl.textContent = this.getMoney(salePrice);

    if (basePrice > salePrice) {
      this.subscriptionCompareEl.textContent = this.getMoney(basePrice);
      this.subscriptionCompareEl.hidden = false;
    } else {
      this.subscriptionCompareEl.textContent = "";
      this.subscriptionCompareEl.hidden = true;
    }
  }

  applyThemeVariant(variant) {
    if (!variant) return;

    this.isSyncing = true;

    const hasSyncedPicker = this.syncVariantPickerInputs(variant);
    if (!hasSyncedPicker && this.variantInput) {
      this.variantInput.value = String(variant.id);
      this.variantInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    window.setTimeout(() => {
      this.isSyncing = false;
    }, 0);
  }

  syncVariantPickerInputs(variant) {
    if (!this.variantOptions || !Array.isArray(variant.options)) return false;

    const fieldsets = Array.from(
      this.variantOptions.querySelectorAll(".wt-product__option fieldset"),
    );
    if (!fieldsets.length) return false;

    let lastMatchedInput = null;

    for (let index = 0; index < variant.options.length; index += 1) {
      const fieldset = fieldsets[index];
      if (!fieldset) return false;

      const targetValue = String(variant.options[index]);
      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (radio) => radio.value === targetValue,
      );
      if (!input) return false;

      input.checked = true;
      lastMatchedInput = input;
    }

    if (lastMatchedInput) {
      lastMatchedInput.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    return false;
  }

  activateSubscribe() {
    const checkedSubscriptionRadio = this.subscriptionVariantRadios.find(
      (radio) => radio.checked,
    );
    const activeVariantId =
      checkedSubscriptionRadio?.dataset.variantId ||
      this.variantInput?.value ||
      this.data.firstSubscriptionVariantId;
    const activeVariant = this.getVariantPayload(String(activeVariantId));
    const selectedVariant =
      activeVariant?.sellingPlan && !this.isExcludedSubscriptionVariant(activeVariant)
        ? activeVariant
        : this.getFirstSubscriptionVariant();

    if (!selectedVariant || !selectedVariant.sellingPlan) return;

    this.subscribeInput.checked = true;
    if (this.subscriptionVariantGrid) {
      this.subscriptionVariantGrid.hidden = false;
    }
    this.markSubscriptionVariant(selectedVariant.id);
    this.applyThemeVariant(selectedVariant);
    this.updateSubscriptionVisuals(selectedVariant);
    this.setSellingPlan(selectedVariant.sellingPlan.id);
    this.updateOneTimeBasePrice();
    this.togglePurchaseTypeRows();
    this.setGalleryOfferMode("subscription", selectedVariant);
    this.syncHabitV2Ui(selectedVariant.daysSupply);
    this.logDebug("subscribe-activated", {
      selectedSubscriptionVariant: selectedVariant.id,
      selectedSubscriptionPlan: selectedVariant.sellingPlan.id,
    });
  }

  activateOneTime() {
    const oneTimeVariant = this.getFirstAvailableVariant();
    if (!oneTimeVariant) return;

    this.clearSubscriptionLineProperty();
    this.oneTimeInput.checked = true;
    if (this.subscriptionVariantGrid) {
      this.subscriptionVariantGrid.hidden = false;
    }
    this.clearSubscriptionVariantSelection();
    this.applyThemeVariant(oneTimeVariant);
    this.updateOneTimeBasePrice();
    this.setSellingPlan(null);
    this.togglePurchaseTypeRows();
    this.setGalleryOfferMode("onetime");
    this.logDebug("onetime-activated", {
      selectedOneTimeVariant: oneTimeVariant.id,
    });
  }
}

const initMarcaSubscriptionWidgets = (scope = document) => {
  const widgets = scope.querySelectorAll(".marca-subscription-widget");
  widgets.forEach((widget) => {
    if (widget.dataset.marcaSubscriptionInit === "true") return;
    widget.dataset.marcaSubscriptionInit = "true";
    const instance = new MarcaSubscriptionWidget(widget);
    instance.init();
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initMarcaSubscriptionWidgets();
  });
} else {
  initMarcaSubscriptionWidgets();
}

document.addEventListener("shopify:section:load", (event) => {
  initMarcaSubscriptionWidgets(event.target);
});
